import { Text, View } from 'react-native';
import { useMemo, useState } from 'react';
import useNotificationBadges from '@/utils/hooks/useNotificationBadges';
import NotificationBadge from '@/components/General/NotificationBadge';
import { StyleSheet } from 'react-native-unistyles';
import Avatar from '@/components/People/Avatar';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'expo-router';
import { dropdownIcons } from '@/components/General/DropdownMenu';
import { deleteBoard, getBoardPermissions, setBoardArchived, toggleBoardHide, toggleBoardPin } from '@/data/connect';
import { showAlert } from '@/utils/showAlert';
import { parseTimeFrame } from '@/utils/dater';
import ResourceCard from '@/components/General/ResourceCard';
import CardGrid from '@/components/General/CardGrid';
import { getImageSource } from '@/utils/FileManager';

/**
 * Reusable boards list for sidebar / standalone display.
 * Shows "My Feed" item at top, then all boards.
 * @param {string} selectedBoardID — board ID to highlight as active (optional)
 * @param {boolean} showMyFeed — whether to show the My Feed item (default true)
 * @param {boolean} showHidden — include boards hidden by the user (default false)
 * @param {boolean} showArchived — include archived boards the user can admin (default false)
 */
const BoardsList = ({ selectedBoardID, showMyFeed = true, display = 'compact', layout, showHidden = false, showArchived = false }) => {
    const router = useRouter();
    const pathname = usePathname();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { boardUnread } = useNotificationBadges();
    const canViewAllBoards = loggedInUser?.can('View All Boards');
    const { data: boardsCurrentUser, isLoading: fetchingBoards, isFetching } = Query.boards.all();
    const [searchText, setSearchText] = useState('');
    const resolvedDisplay = layout || display;
    const compact = resolvedDisplay === 'compact';

    const togglePinMutation = useMutation({
        mutationFn: toggleBoardPin,
        onSuccess: (data) => {
            queryClient.setQueryData(QueryKeys.boards.all(), old => {
                return old?.map(b => (b.id == data.boardID) ? { ...b, pinnedForUser: data.isPinned } : b);
            });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: deleteBoard,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.boards.all() });
            const deletedBoardIsCurrent = selectedBoardID === variables.boardID
                || pathname?.includes(`/connect/boards/${variables.boardID}/`);
            if (deletedBoardIsCurrent) router.replace('/connect');
        },
    });

    const toggleHideMutation = useMutation({
        mutationFn: toggleBoardHide,
        onSuccess: (data) => {
            queryClient.setQueryData(QueryKeys.boards.all(), old => {
                return old?.map(b => (b.id == data.boardID) ? { ...b, hiddenForUser: data.isHidden, mutedForUser: data.isMuted ?? data.isHidden } : b);
            });
        }
    });

    const archiveMutation = useMutation({
        mutationFn: setBoardArchived,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.boards.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.boards.byId(variables.boardID) });
            // Drop the archived board's posts from My Feed cache immediately.
            queryClient.invalidateQueries({ queryKey: QueryKeys.posts.myBoardsFeed() });
            // Archiving removes the board for everyone — leave its feed if open.
            const archivedBoardIsCurrent = variables.isArchived
                && (selectedBoardID === variables.boardID
                    || pathname?.includes(`/connect/boards/${variables.boardID}/`));
            if (archivedBoardIsCurrent) router.replace('/connect');
        },
    });

    //Hidden boards drop out unless showHidden is on. Archived boards only ever
    //surface via showArchived AND only where the viewer can admin the board —
    //non-admins never see archived boards.
    const boards = useMemo(() => (boardsCurrentUser || []).filter(b => {
        if(!['MESSAGE_BOARD', 'ANNOUNCEMENTS'].includes(b.type)) return false;
        if(b.isArchived){
            const perms = getBoardPermissions(b, loggedInUser);
            if(!showArchived || (!perms.isAdmin && !loggedInUser?.isAdmin)) return false;
        }
        if(b.type !== 'ANNOUNCEMENTS' && b.hiddenForUser && !showHidden) return false;
        return true;
    }), [boardsCurrentUser, showHidden, showArchived, loggedInUser]);

    const filteredBoards = useMemo(() => {
        if(!searchText) return boards;
        const q = searchText.toLowerCase();
        return boards.filter(b => (b.name?.toLowerCase().includes(q) || b.description?.toLowerCase().includes(q)));
    }, [boards, searchText]);

    // Determine board membership: open boards (no roles) or boards where the
    // user has a matching role. Non-member boards go in a separate section.
    const userRoleIds = useMemo(() => new Set(
        (loggedInUser?.Roles || loggedInUser?.roles || []).map(r => r.id || r.roleID).filter(Boolean)
    ), [loggedInUser]);
    const isBoardMember = (board) => {
        if (board.createdByID && board.createdByID === loggedInUser?.id) return true;
        if (!board.Roles || board.Roles.length === 0) return true;
        return board.Roles.some(r => userRoleIds.has(r.roleID));
    };
    const memberBoards = useMemo(() => filteredBoards.filter(isBoardMember), [filteredBoards, userRoleIds]);
    const otherBoards = useMemo(() => canViewAllBoards ? filteredBoards.filter(b => !isBoardMember(b)) : [], [filteredBoards, userRoleIds, canViewAllBoards]);
    const announcementBoards = useMemo(() => memberBoards.filter(b => b.type === 'ANNOUNCEMENTS'), [memberBoards]);
    const pinnedBoards = useMemo(() => memberBoards.filter(b => b.pinnedForUser && b.type !== 'ANNOUNCEMENTS'), [memberBoards]);
    const unpinnedMemberBoards = useMemo(() => memberBoards.filter(b => !b.pinnedForUser && b.type !== 'ANNOUNCEMENTS'), [memberBoards]);

    const listData = fetchingBoards ? [] : [
        ...(showMyFeed ? [{ __type: 'myFeed', id: '__myFeed__' }] : []),
        ...announcementBoards,
        ...(pinnedBoards.length > 0 ? ["Pinned", ...pinnedBoards] : []),
        ...(unpinnedMemberBoards.length > 0 ? ["All My Boards", ...unpinnedMemberBoards] : []),
        ...(otherBoards.length > 0 ? ['Other Boards', ...otherBoards] : []),
    ];

    const renderItem = ({ item }) => {
        if(typeof item === 'string') {
            return (
                <View style={styles.sectionHeader}>
                    <Text style={styles.sectionHeaderText}>{item}</Text>
                </View>
            );
        }
        if(item.__type === 'myFeed') {
            const isActive = selectedBoardID === '__myFeed__';
            return (
                <ResourceCard
                    variant={compact ? 'compact' : 'hero'}
                    heroSeed="my-feed"
                    eyebrow="Connect"
                    title="My Feed"
                    subtitle="All posts from your boards"
                    badges={[{ label: 'All boards', icon: 'newspaper-outline', colorVariant: 'connect' }]}
                    active={isActive}
                    moduleColor="connect"
                    onPress={() => router.push('/connect/boards/feed')}
                    testID="BoardCard-MyFeed"
                />
            );
        }
        const unreadCount = boardUnread(item.id);
        const isActive = selectedBoardID === item.id;
        const member = isBoardMember(item);
        const boardPermissions = getBoardPermissions(item, loggedInUser);
        const avatar = item.lastPoster
            ? <Avatar disableBorder size={compact ? 36 : 48} profile={item.lastPoster} />
            : null;
        const heroPath = item.avatarPath;
        const heroImage = getImageSource(heroPath, heroPath ? `board-${item.id}-${heroPath}` : undefined);
        const lastPostDate = item.lastPostDate ? parseTimeFrame(item.lastPostDate, false) : null;
        const badges = [
            <NotificationBadge key="unread" count={unreadCount} size={20} testID={`board-badge-${item.id}`} />,
            item.type === 'ANNOUNCEMENTS' ? { key: 'built-in', label: 'Your audiences', icon: 'megaphone-outline', colorVariant: 'connect' } : item.Roles?.length == 0 && { key: 'open', label: 'Open board', icon: 'people', colorVariant: 'textSecondary' },
            item.hiddenForUser && { key: 'hidden', label: 'Hidden', icon: 'eye-off-outline', library: 'ionicon', colorVariant: 'textSecondary', testID: `board-${item.id}-hidden-icon` },
            item.isArchived && { key: 'archived', label: 'Archived', icon: 'archive-outline', library: 'ionicon', colorVariant: 'textSecondary', testID: `board-${item.id}-archived-icon` },
            item.mutedForUser && { key: 'muted', label: 'Muted', icon: 'notifications-off-outline', library: 'ionicon', colorVariant: 'textSecondary', testID: `board-${item.id}-muted-icon` },
        ].filter(Boolean);
        const menuOptions = [];
        if (member) {
            menuOptions.push({
                name: item.pinnedForUser ? 'Unpin Board' : 'Pin Board',
                icon: item.pinnedForUser ? dropdownIcons.unpin : dropdownIcons.pin,
                onSelect: () => {
                    setTimeout(() => togglePinMutation.mutate({ boardID: item.id, personID: loggedInUser?.id, isPinned: !item.pinnedForUser }), 250);
                }
            });
            if(item.type !== 'ANNOUNCEMENTS') menuOptions.push({
                name: item.hiddenForUser ? 'Unhide Board' : 'Hide Board',
                icon: item.hiddenForUser ? dropdownIcons.unhide : dropdownIcons.hide,
                onSelect: () => {
                    setTimeout(() => toggleHideMutation.mutate({ boardID: item.id, personID: loggedInUser?.id, isHidden: !item.hiddenForUser }), 250);
                }
            });
        }
        if (item.type === 'ANNOUNCEMENTS' && loggedInUser?.can?.('Send SMS Messages')) {
            menuOptions.push({ name: 'New announcement', icon: dropdownIcons.megaphone, onSelect: () => router.push('/connect/announcements/create') });
            menuOptions.push({ name: 'Announcement settings', icon: dropdownIcons.settings, onSelect: () => router.push('/connect/announcements/settings') });
        }
        if (item.type !== 'ANNOUNCEMENTS' && (loggedInUser?.isAdmin || boardPermissions.isAdmin)) {
            menuOptions.push({
                name: 'Edit Board',
                icon: { android: 'create', ios: 'pencil', web: 'create-outline' },
                onSelect: () => router.push({ pathname: '/connect/boards/create', params: { id: item.id, type: 'MESSAGE_BOARD' } }),
            });
            menuOptions.push({
                name: item.isArchived ? 'Unarchive Board' : 'Archive Board',
                icon: item.isArchived ? { android: 'unarchive', ios: 'arrow.up.bin', web: 'archive-outline' } : dropdownIcons.archive,
                onSelect: () => {
                    if(item.isArchived){
                        setTimeout(() => archiveMutation.mutate({ boardID: item.id, isArchived: false }), 250);
                        return;
                    }
                    showAlert({
                        title: 'Archive Board',
                        subtitle: 'Archiving removes this board from everyone\'s view and blocks new posts, comments, and reactions. You can unarchive it later.',
                        isDestructive: true,
                        onConfirm: () => archiveMutation.mutate({ boardID: item.id, isArchived: true }),
                        confirmText: 'Archive',
                    });
                },
                destructive: !item.isArchived,
            });
            menuOptions.push({
                name: 'Delete Board',
                icon: { android: 'delete', ios: 'trash', web: 'trash-outline' },
                onSelect: () => {
                    showAlert({
                        title: 'Delete Board',
                        subtitle: 'Are you sure you want to delete this board? This will also delete all posts in the board and you will not be able to recover them.',
                        isDestructive: true,
                        onConfirm: () => deleteMutation.mutate({ boardID: item.id }),
                        confirmText: 'Delete',
                    });
                },
                destructive: true,
            });
        }

        return (
            <ResourceCard
                variant={compact ? 'compact' : 'hero'}
                heroSeed={item.id}
                heroImage={heroImage}
                avatar={avatar}
                eyebrow={item.type === 'ANNOUNCEMENTS' ? 'Built-in Board' : item.pinnedForUser ? 'Pinned board' : 'Board'}
                title={item.name}
                subtitle={item.description}
                date={lastPostDate}
                badges={badges}
                active={isActive}
                moduleColor="connect"
                onPress={() => router.push(`/connect/boards/${item.id}/feed`)}
                menuOptions={menuOptions}
                testID={`BoardCard-${item.id}`}
            />
        );
    };

    let contentLoaders = [];
    if(fetchingBoards) {
        for(let i = 0; i < 8; i++) {
            contentLoaders.push(<View key={"loader" + i} style={styles.loaderRow}><ContentLoader containerHeight={compact ? 88 : 230} staggerPosition={i} /></View>);
        }
    }

    return (
        <View style={styles.container}>
            <View style={styles.searchContainer}>
                <SearchBox
                    searchText={searchText}
                    onChangeText={setSearchText}
                    placeholder="Search boards"
                    textChangeDelay={150}
                    testID="BoardsList"
                />
            </View>
            <CardGrid
                keyExtractor={(item) => typeof item === 'string' ? item : (item.id || item.__type)}
                estimatedItemSize={compact ? 96 : 230}
                columns={compact ? 1 : { xs: 2, md: 3, xl: 4 }}
                gap={compact ? 10 : 8}
                padding={8}
                data={listData}
                renderItem={renderItem}
                getItemType={(item) => typeof item === 'string' ? 'sectionHeader' : (item.__type || 'board')}
                isFullSpan={(item) => typeof item === 'string'}
                progressViewOffset={1000}
                refreshing={isFetching && !fetchingBoards}
                onRefresh={() => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.boards.all() });
                    queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
                }}
                ListFooterComponent={!fetchingBoards
                    ? <View style={styles.footer}>{memberBoards.length == 0 && otherBoards.length == 0 ? <EmptyListPlaceholder icon="newspaper-outline" spacing={10} message={searchText ? "No boards match your search" : "No boards yet!"} /> : <View />}</View>
                    : <View>{contentLoaders}</View>}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing }) => ({
    container: {
        flex: 1,
    },
    searchContainer: {
        paddingHorizontal: spacing.sm,
        paddingTop: spacing.sm,
        paddingBottom: spacing.xs,
    },
    sectionHeader: {
        paddingHorizontal: spacing.xs,
        paddingTop: spacing.md,
        paddingBottom: spacing.xs,
    },
    sectionHeaderText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    loaderRow: {
        marginHorizontal: spacing.sm,
        marginVertical: spacing.xs,
    },
    footer: {
        paddingBottom: 80,
    },
}));

export default BoardsList;
